import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const BookingCalendar = ({ onBooking, pricing = {} }) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState('single');
  const [guests, setGuests] = useState(1);

  const currentDate = new Date();
  const [currentMonth, setCurrentMonth] = useState(currentDate.getMonth());
  const [currentYear, setCurrentYear] = useState(currentDate.getFullYear());

  const packages = [
    {
      id: 'single',
      name: 'Single Session',
      price: pricing.singleSession || 150,
      duration: '2 hours',
      description: 'Perfect for first-time experience'
    },
    {
      id: 'day',
      name: 'Day Pass',
      price: pricing.dayPass || 300,
      duration: 'Full day',
      description: 'Unlimited sessions for one day'
    },
    {
      id: 'weekly',
      name: 'Weekly Package',
      price: pricing.weeklyPackage || 1200,
      duration: '7 days',
      description: 'Best value for intensive healing'
    }
  ];

  const timeSlots = [
    '09:00 AM', '11:00 AM', '01:00 PM', '03:00 PM', '05:00 PM', '07:00 PM'
  ];

  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const isDateAvailable = (day) => {
    const date = new Date(currentYear, currentMonth, day);
    return date >= currentDate;
  };

  const handleDateClick = (day) => {
    if (isDateAvailable(day)) {
      setSelectedDate(new Date(currentYear, currentMonth, day));
      setSelectedTime(null);
    }
  };

  const handleBooking = () => {
    if (selectedDate && selectedTime && selectedPackage) {
      const bookingData = {
        date: selectedDate,
        time: selectedTime,
        package: packages.find(p => p.id === selectedPackage),
        guests: guests,
        totalPrice: packages.find(p => p.id === selectedPackage)?.price * guests
      };
      onBooking?.(bookingData);
    }
  };

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const daysInMonth = getDaysInMonth(currentMonth, currentYear);
  const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
  const selectedPackageData = packages.find(p => p.id === selectedPackage);

  return (
    <div className="bg-surface rounded-lg shadow-elevation-1 p-6">
      <h3 className="text-xl font-semibold text-text-primary mb-6">Book Your EES Session</h3>
      
      {/* Package Selection */}
      <div className="mb-6">
        <h4 className="text-md font-medium text-text-primary mb-3">Choose Package</h4>
        <div className="grid grid-cols-1 gap-3">
          {packages.map((pkg) => (
            <button
              key={pkg.id}
              onClick={() => setSelectedPackage(pkg.id)}
              className={`text-left p-4 rounded-lg border-2 transition-all duration-200 ${
                selectedPackage === pkg.id
                  ? 'border-primary bg-primary-50' :'border-border hover:border-primary/50 hover:bg-primary-50/50'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <h5 className="font-medium text-text-primary">{pkg.name}</h5>
                <span className="text-lg font-bold text-primary">${pkg.price}</span>
              </div>
              <div className="text-sm text-text-secondary mb-1">{pkg.duration}</div>
              <div className="text-sm text-text-secondary">{pkg.description}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Guest Count */}
      <div className="mb-6">
        <h4 className="text-md font-medium text-text-primary mb-3">Number of Guests</h4>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setGuests(Math.max(1, guests - 1))}
            className="w-10 h-10 bg-secondary-100 rounded-full flex items-center justify-center hover:bg-secondary-200 transition-colors duration-200"
            disabled={guests <= 1}
          >
            <Icon name="Minus" size={16} />
          </button>
          <span className="text-lg font-medium text-text-primary min-w-[2rem] text-center">{guests}</span>
          <button
            onClick={() => setGuests(Math.min(6, guests + 1))}
            className="w-10 h-10 bg-secondary-100 rounded-full flex items-center justify-center hover:bg-secondary-200 transition-colors duration-200"
            disabled={guests >= 6}
          >
            <Icon name="Plus" size={16} />
          </button>
        </div>
      </div>

      {/* Calendar */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-md font-medium text-text-primary">Select Date</h4>
          <div className="flex items-center space-x-2">
            <button
              onClick={handlePrevMonth}
              className="p-2 hover:bg-secondary-100 rounded-md transition-colors duration-200"
            >
              <Icon name="ChevronLeft" size={16} />
            </button>
            <span className="font-medium text-text-primary min-w-[150px] text-center">
              {monthNames[currentMonth]} {currentYear}
            </span>
            <button
              onClick={handleNextMonth}
              className="p-2 hover:bg-secondary-100 rounded-md transition-colors duration-200"
            >
              <Icon name="ChevronRight" size={16} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-1 text-center">
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
            <div key={day} className="p-2 text-sm font-medium text-text-secondary">
              {day}
            </div>
          ))}
          
          {[...Array(firstDay)].map((_, index) => (
            <div key={index} className="p-2"></div>
          ))}
          
          {[...Array(daysInMonth)].map((_, index) => {
            const day = index + 1;
            const isAvailable = isDateAvailable(day);
            const isSelected = selectedDate && selectedDate.getDate() === day && 
                             selectedDate.getMonth() === currentMonth && 
                             selectedDate.getFullYear() === currentYear;
            
            return (
              <button
                key={day}
                onClick={() => handleDateClick(day)}
                disabled={!isAvailable}
                className={`p-2 text-sm rounded-md transition-all duration-200 ${
                  isSelected
                    ? 'bg-primary text-white'
                    : isAvailable
                    ? 'hover:bg-primary-50 text-text-primary' :'text-text-secondary cursor-not-allowed opacity-50'
                }`}
              >
                {day}
              </button>
            );
          })}
        </div>
      </div>

      {/* Time Selection */}
      {selectedDate && (
        <div className="mb-6">
          <h4 className="text-md font-medium text-text-primary mb-3">Select Time</h4>
          <div className="grid grid-cols-2 gap-2">
            {timeSlots.map((time) => (
              <button
                key={time}
                onClick={() => setSelectedTime(time)}
                className={`p-3 text-sm rounded-md border transition-all duration-200 ${
                  selectedTime === time
                    ? 'bg-primary text-white border-primary' :'bg-surface text-text-primary border-border hover:border-primary hover:bg-primary-50'
                }`}
              >
                {time}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Booking Summary */}
      {selectedDate && selectedTime && (
        <div className="bg-primary-50 border border-primary-100 rounded-lg p-4 mb-6">
          <h4 className="font-medium text-primary mb-2">Booking Summary</h4>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-primary-700">Package:</span>
              <span className="text-primary">{selectedPackageData?.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-primary-700">Date:</span>
              <span className="text-primary">{selectedDate.toLocaleDateString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-primary-700">Time:</span>
              <span className="text-primary">{selectedTime}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-primary-700">Guests:</span>
              <span className="text-primary">{guests}</span>
            </div>
            <div className="flex justify-between font-medium text-primary pt-2 border-t border-primary-200">
              <span>Total:</span>
              <span>${(selectedPackageData?.price || 0) * guests}</span>
            </div>
          </div>
        </div>
      )}

      {/* Book Now Button */}
      <button
        onClick={handleBooking}
        disabled={!selectedDate || !selectedTime}
        className="w-full bg-primary text-white py-4 rounded-md font-medium hover:bg-primary-700 disabled:bg-secondary-300 disabled:cursor-not-allowed transition-all duration-200"
      >
        {selectedDate && selectedTime ? 'Book Now' : 'Select Date & Time'}
      </button>
    </div>
  );
};

export default BookingCalendar;